import React, { useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';

// Рендерит DOM-узел чека (от логотипа до печати) в canvas. Одна отрисовка —
// а из неё уже получаем и PNG (для локального сохранения/Web Share), и JPEG
// (для отправки боту), не гоняя тяжёлый html2canvas несколько раз.
async function renderReceiptCanvas(node) {
  return html2canvas(node, {
    backgroundColor: getComputedStyle(node).backgroundColor || '#ffffff',
    scale: Math.min(window.devicePixelRatio || 2, 3),
    useCORS: true
  });
}

// canvas.toBlob иногда вообще не вызывает колбэк (например, если холст
// оказался "заражён" кросс-доменным ресурсом) — оборачиваем в промис со
// своим таймаутом, чтобы такой случай гарантированно завершался ошибкой,
// а не зависанием.
function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Не удалось отрисовать чек (таймаут)')), 8000);
    canvas.toBlob((b) => {
      clearTimeout(timer);
      b ? resolve(b) : reject(new Error('Не удалось создать изображение чека'));
    }, type, quality);
  });
}

// Рендерит DOM-узел чека в PNG-файл, готовый к скачиванию/Web Share.
async function renderReceiptToFile(node) {
  const canvas = await renderReceiptCanvas(node);
  const blob = await canvasToBlob(canvas, 'image/png');
  return new File([blob], 'receipt.png', { type: 'image/png' });
}

// Превращает File/Blob в data URL (base64) для отправки на сервер.
function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Не удалось прочитать изображение чека'));
    reader.readAsDataURL(blob);
  });
}

// JPEG того же canvas'а. Именно JPEG принимает Telegram и для sendPhoto по URL,
// и для инлайн-фото (InlineQueryResultPhoto требует "Photo must be in JPEG
// format" по документации Bot API) — раньше мы слали PNG, и Telegram молча
// отбрасывал результат, из-за чего "вставлялась ссылка, которая не отправляется".
async function canvasToJpegDataUrl(canvas) {
  const blob = await canvasToBlob(canvas, 'image/jpeg', 0.92);
  return blobToDataURL(blob);
}

// tg_id текущего игрока — тот же источник, что и во всём приложении (см. App.js).
// Нужен, чтобы бот мог отправить готовый чек прямо в личный чат игрока.
function getCurrentTgId() {
  const u = window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initDataUnsafe && window.Telegram.WebApp.initDataUnsafe.user;
  return u && u.id ? String(u.id) : null;
}

// Форматирование даты транзакции (хранится в БД как UTC-строка без таймзоны)
export function formatHistoryDate(timestamp) {
  if (!timestamp) return 'Нет данных';
  const utcDateStr = timestamp.replace(' ', 'T') + 'Z';
  return new Date(utcDateStr).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow', dateStyle: 'short', timeStyle: 'short' });
}

// Определяет, "исходящая" ли операция для текущего контекста.
// Обычного сравнения по tg_id недостаточно: если игрок переводит сам себе
// между двумя СВОИМИ счетами, from_tg_id и to_tg_id совпадают — тогда, если
// известно, историю какого именно счёта сейчас смотрят (currentAccountNumber),
// направление определяется по номеру счёта, а не по игроку.
function isOutgoingFor(item, currentTgId, currentAccountNumber) {
  if (currentAccountNumber !== undefined && currentAccountNumber !== null) {
    if (String(item.from_account) === String(currentAccountNumber)) return true;
    if (String(item.to_account) === String(currentAccountNumber)) return false;
  }
  return String(item.from_tg_id) === String(currentTgId);
}

// Готовит данные для модалки чека по одной записи истории.
// currentTgId нужен только для определения направления (исходящая/входящая) у некоторых типов.
// currentAccountNumber (необязателен) — номер счёта, историю которого сейчас смотрят;
// нужен, чтобы правильно определить направление при переводе самому себе между своими счетами.
export function buildReceiptData(item, currentTgId, currentAccountNumber) {
  let status = 'Успешно';
  let title = 'Перевод';
  let account = item.from_account || item.to_account;
  let senderAccount = null;
  let receiverAccount = null;
  let comment = null;
  let destType = null;
  let destValue = null;
  let reason = null;

  if (item.type === 'WITHDRAW') {
    title = 'Вывод средств';
    const parts = item.comment.split('|');
    const wStatus = parts[0];
    if (wStatus === 'COMPLETED') status = 'Успешно';
    else if (wStatus === 'REJECTED') status = 'Отклонено';
    else status = 'В обработке';
    account = item.from_account;
    reason = parts[1];
    destType = parts[2] === 'MAIL' ? 'Почта' : 'Счет MP';
    destValue = parts[3];
  } else if (item.type === 'DEPOSIT_REQ') {
    title = 'Пополнение';
    const parts = item.comment.split('|');
    const dStatus = parts[0];
    if (dStatus === 'COMPLETED') status = 'Успешно';
    else if (dStatus === 'REJECTED') status = 'Отклонено';
    else status = 'В обработке';
    account = item.from_account;
    reason = parts[1];
    destType = parts[2] === 'MAIL' ? 'Почта' : 'Счет MP';
    destValue = parts[3];
  } else if (item.type === 'DEPOSIT' || item.type === 'WITHDRAW_REFUND') {
    title = item.type === 'DEPOSIT' ? 'Пополнение' : 'Возврат средств';
    account = item.to_account;
    comment = item.comment;
  } else if (item.type === 'TRANSFER') {
    const isOutgoing = isOutgoingFor(item, currentTgId, currentAccountNumber);
    title = isOutgoing ? 'Исходящий перевод' : 'Входящий перевод';
    senderAccount = item.from_account;
    receiverAccount = item.to_account;
    comment = item.comment;
  } else if (item.type === 'INVOICE') {
    title = 'Оплата инвойса';
    account = item.from_account;
    const parts = (item.comment || '').split('|');
    reason = parts[1];
  } else if (item.type === 'FINE_PAYMENT') {
    title = 'Оплата инвойса';
    senderAccount = item.from_account;
    receiverAccount = item.to_account;
    comment = item.comment;
  } else if (item.type === 'AD_PURCHASE') {
    title = 'Оплата рекламы';
    senderAccount = item.from_account;
    receiverAccount = item.to_account;
    comment = item.comment;
  } else if (item.type === 'AD_REFUND') {
    title = 'Возврат за рекламу';
    account = item.to_account;
    comment = item.comment;
  } else if (item.type === 'ITEM_PROMOTION') {
    title = 'Продвижение товара';
    senderAccount = item.from_account;
    receiverAccount = item.to_account;
    comment = item.comment;
  } else if (item.type === 'CARD_DESIGN_PURCHASE') {
    title = 'Дизайн карты';
    senderAccount = item.from_account;
    receiverAccount = item.to_account;
    comment = item.comment;
  } else if (item.type === 'ORDER_PAYMENT') {
    title = 'Оформление заказа';
    account = item.from_account;
    comment = item.comment;
    if (item.order_status === 'DELIVERED') status = 'Успешно';
    else if (item.order_status === 'CANCELLED') status = 'Отклонено';
    else status = 'В обработке';
  } else if (item.type === 'ORDER_REFUND') {
    title = 'Возврат за заказ';
    account = item.to_account;
    comment = item.comment;
  } else if (item.type === 'MARKET_SALE') {
    title = 'Продажа товара';
    account = item.to_account;
    comment = item.comment;
  } else if (item.type === 'TREASURY_INCOME') {
    title = 'Поступление в казну';
    account = item.to_account;
    comment = item.comment;
  } else {
    title = 'Операция';
  }

  return {
    date: formatHistoryDate(item.timestamp),
    amount: item.amount,
    status,
    reqId: item.req_id || 'Нет',
    account,
    title,
    type: item.type,
    senderAccount,
    receiverAccount,
    comment,
    destType,
    destValue,
    reason
  };
}

/**
 * Рендер одной записи истории операций.
 * ЕДИНСТВЕННОЕ место, где нужно добавлять обработку нового типа транзакции —
 * и в банке пользователя, и в админ-панели он появится сам собой.
 *
 * props:
 *  - item: запись истории
 *  - currentTgId: tg_id того, для кого отображается история (для направления исходящая/входящая)
 *  - isDeletedAccountOp: подсвечивать ли строку как операцию по удалённому счёту
 *  - onReceiptClick(item): открыть чек
 *  - onPayInvoice(invoiceId): опционально — кнопка "Оплатить" у неоплаченного инвойса (только в банке игрока)
 */
export function HistoryItem({ item, currentTgId, currentAccountNumber, isDeletedAccountOp, onReceiptClick, onPayInvoice }) {
  const date = formatHistoryDate(item.timestamp);
  const itemClass = `history-item ${isDeletedAccountOp ? 'deleted-history' : ''}`;
  const receiptBtn = item.req_id ? (
    <button className="receipt-btn" style={{margin: '0 0 0 10px', flexShrink: 0}} onClick={() => onReceiptClick(item)}>🧾 Чек</button>
  ) : null;

  if (item.type === 'CREATE_ACCOUNT') {
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta"><span>Создание счета</span> <span>{date}</span></div>
        <div className="history-body">Счет #{item.from_account} успешно создан</div>
      </div>
    );
  } else if (item.type === 'UPDATE_ACCOUNT' || item.type === 'UPDATE_ACCOUNT_ADMIN') {
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta"><span>Изменение настроек</span> <span>{date}</span></div>
        <div className="history-body">Настройки счета #{item.from_account} изменены</div>
        {item.comment && <div style={{fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px', fontStyle: 'italic'}}>{item.comment}</div>}
      </div>
    );
  } else if (item.type === 'DELETE_ACCOUNT' || item.type === 'DELETE_ACCOUNT_ADMIN') {
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta"><span>Удаление счета</span> <span>{date}</span></div>
        <div className="history-body">Счет #{item.from_account} удален</div>
        {item.comment && <div style={{fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px', fontStyle: 'italic'}}>{item.comment}</div>}
      </div>
    );
  } else if (item.type === 'BLOCK_ACCOUNT') {
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta"><span>Блокировка счета</span> <span>{date}</span></div>
        <div className="history-body">Счет #{item.from_account} заблокирован</div>
        {item.comment && <div style={{fontSize: '12px', color: 'var(--danger)', marginTop: '4px', fontStyle: 'italic'}}>{item.comment}</div>}
      </div>
    );
  } else if (item.type === 'UNBLOCK_ACCOUNT') {
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta"><span>Разблокировка счета</span> <span>{date}</span></div>
        <div className="history-body">Счет #{item.from_account} разблокирован</div>
      </div>
    );
  } else if (item.type === 'DEPOSIT') {
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta"><span>Пополнение</span> <span>{date}</span></div>
        <div className="history-body">
          На счет #{item.to_account} <br/>
          <span style={{color: 'var(--success)', fontWeight: 'bold'}}>+{item.amount} сц</span>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '4px'}}>
            <div style={{flex: 1}}>
              {item.comment && <div style={{fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic'}}>{item.comment}</div>}
            </div>
            {receiptBtn}
          </div>
        </div>
      </div>
    );
  } else if (item.type === 'WITHDRAW' || item.type === 'DEPOSIT_REQ') {
    const parts = (item.comment || '').split('|');
    const status = parts[0];
    const rejectReason = parts[1];
    const destType = parts[2];
    const destValue = parts[3];

    let statusText = '⏳ В обработке';
    let statusColor = 'var(--warning)';
    if (status === 'COMPLETED') { statusText = '✅ Одобрено'; statusColor = 'var(--success)'; }
    if (status === 'REJECTED') { statusText = '❌ Отклонено'; statusColor = 'var(--danger)'; }

    const isWithdraw = item.type === 'WITHDRAW';
    const amountColor = status === 'REJECTED' ? 'var(--text-secondary)' : (status === 'COMPLETED' ? (isWithdraw ? 'var(--danger)' : 'var(--success)') : 'var(--text-secondary)');

    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta"><span>{isWithdraw ? 'Вывод средств' : 'Пополнение'}</span> <span>{date}</span></div>
        <div className="history-body">
          Заявка на {isWithdraw ? 'вывод' : 'пополнение'} ({destType === 'MAIL' ? 'Почта' : 'Счет MP'}: {destValue})<br/>
          <span style={{color: amountColor, fontWeight: 'bold'}}>{isWithdraw ? '-' : '+'}{item.amount} сц</span><br/>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '4px'}}>
            <div style={{flex: 1}}>
              Статус: <span style={{color: statusColor, fontWeight: 'bold', whiteSpace: 'nowrap'}}>{statusText}</span>
              {status === 'REJECTED' && rejectReason && <div style={{fontSize: '12px', color: 'var(--danger)', marginTop: '4px'}}>Причина: {rejectReason}</div>}
            </div>
            {receiptBtn}
          </div>
        </div>
      </div>
    );
  } else if (item.type === 'WITHDRAW_REFUND') {
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta"><span>Возврат средств</span> <span>{date}</span></div>
        <div className="history-body">
          На счет #{item.to_account} <br/>
          <span style={{color: 'var(--success)', fontWeight: 'bold'}}>+{item.amount} сц</span>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '4px'}}>
            <div style={{fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', flex: 1, wordBreak: 'break-word'}}>{item.comment}</div>
            {receiptBtn}
          </div>
        </div>
      </div>
    );
  } else if (item.type === 'TRANSFER') {
    const isOutgoing = isOutgoingFor(item, currentTgId, currentAccountNumber);
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta">
          <span>{isOutgoing ? 'Исходящий перевод' : 'Входящий перевод'}</span>
          <span>{date}</span>
        </div>
        <div className="history-body">
          {isOutgoing ? (
            <>
              Со счета #{item.from_account}{item.to_account ? <> на счет #{item.to_account} ({item.to_nickname})</> : <> — оплата услуги</>} <br/>
              <span style={{color: 'var(--danger)', fontWeight: 'bold'}}>-{item.amount} сц</span>
            </>
          ) : (
            <>
              От {item.from_nickname} (#{item.from_account}) на счет #{item.to_account} <br/>
              <span style={{color: 'var(--success)', fontWeight: 'bold'}}>+{item.amount} сц</span>
            </>
          )}
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '4px'}}>
            <div style={{flex: 1}}>
              {item.comment && <div style={{fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic'}}>«{item.comment}»</div>}
            </div>
            {receiptBtn}
          </div>
        </div>
      </div>
    );
  } else if (item.type === 'INVOICE') {
    const parts = (item.comment || '').split('|');
    const status = parts[0];
    const reason = parts[1];
    const destAccount = parts[2];
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta">
          <span style={{color: status === 'PENDING' ? 'var(--danger)' : 'var(--text-secondary)', fontWeight: 'bold'}}>
            {status === 'PENDING' ? 'Неоплаченный инвойс' : 'Инвойс (Оплачен)'}
          </span>
          <span>{date}</span>
        </div>
        <div className="history-body">
          {destAccount ? <>Оплатить на счет: #{destAccount}</> : <>Оплата услуги Azon</>} <br/>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '4px'}}>
            <div>
              <span style={{color: status === 'PENDING' ? 'var(--danger)' : 'var(--text-secondary)', fontWeight: 'bold'}}>{item.amount} сц</span>
              <div style={{marginTop: '4px', fontSize: '12px', color: 'var(--text-secondary)'}}>{reason}</div>
            </div>
            {status === 'PENDING' && onPayInvoice && (
              <button className="primary-btn" style={{background: 'var(--success)', padding: '6px 12px', fontSize: '12px', width: 'auto', flexShrink: 0, margin: '0 0 2px 10px'}} onClick={() => onPayInvoice(item.id)}>Оплатить</button>
            )}
          </div>
        </div>
      </div>
    );
  } else if (item.type === 'FINE_PAYMENT') {
    const isOutgoing = isOutgoingFor(item, currentTgId, currentAccountNumber);
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta">
          <span>{isOutgoing ? 'Оплата инвойса' : 'Поступление (Инвойс)'}</span>
          <span>{date}</span>
        </div>
        <div className="history-body">
          {isOutgoing ? (
            <>
              Со счета #{item.from_account}{item.to_account ? <> на счет #{item.to_account}</> : null} <br/>
              <span style={{color: 'var(--danger)', fontWeight: 'bold'}}>-{item.amount} сц</span>
            </>
          ) : (
            <>
              От {item.from_nickname} (#{item.from_account}) на счет #{item.to_account} <br/>
              <span style={{color: 'var(--success)', fontWeight: 'bold'}}>+{item.amount} сц</span>
            </>
          )}
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '4px'}}>
            <div style={{fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', flex: 1, wordBreak: 'break-word'}}>{item.comment}</div>
            {receiptBtn}
          </div>
        </div>
      </div>
    );
  } else if (item.type === 'AD_PURCHASE') {
    const isOutgoing = isOutgoingFor(item, currentTgId, currentAccountNumber);
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta">
          <span>{isOutgoing ? 'Оплата рекламы' : 'Поступление (Реклама)'}</span>
          <span>{date}</span>
        </div>
        <div className="history-body">
          {isOutgoing ? (
            <>
              Счет #{item.from_account}{item.to_account ? <> на счет #{item.to_account}</> : null} <br/>
              <span style={{color: 'var(--danger)', fontWeight: 'bold'}}>-{item.amount} сц</span>
            </>
          ) : (
            <>
              От счета #{item.from_account} на счет #{item.to_account} <br/>
              <span style={{color: 'var(--success)', fontWeight: 'bold'}}>+{item.amount} сц</span>
            </>
          )}
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '4px'}}>
            <div style={{fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', flex: 1, wordBreak: 'break-word'}}>{item.comment}</div>
            {receiptBtn}
          </div>
        </div>
      </div>
    );
  } else if (item.type === 'AD_REFUND') {
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta">
          <span>Возврат за рекламу</span>
          <span>{date}</span>
        </div>
        <div className="history-body">
          На счет #{item.to_account} <br/>
          <span style={{color: 'var(--success)', fontWeight: 'bold'}}>+{item.amount} сц</span>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '4px'}}>
            <div style={{fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', flex: 1, wordBreak: 'break-word'}}>{item.comment}</div>
            {receiptBtn}
          </div>
        </div>
      </div>
    );
  } else if (item.type === 'ITEM_PROMOTION') {
    const isOutgoing = isOutgoingFor(item, currentTgId, currentAccountNumber);
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta">
          <span>{isOutgoing ? 'Продвижение товара' : 'Поступление (Продвижение)'}</span>
          <span>{date}</span>
        </div>
        <div className="history-body">
          {isOutgoing ? (
            <>
              Счет #{item.from_account}{item.to_account ? <> на счет #{item.to_account}</> : null} <br/>
              <span style={{color: 'var(--danger)', fontWeight: 'bold'}}>-{item.amount} сц</span>
            </>
          ) : (
            <>
              От счета #{item.from_account} на счет #{item.to_account} <br/>
              <span style={{color: 'var(--success)', fontWeight: 'bold'}}>+{item.amount} сц</span>
            </>
          )}
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '4px'}}>
            <div style={{fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', flex: 1, wordBreak: 'break-word'}}>{item.comment}</div>
            {receiptBtn}
          </div>
        </div>
      </div>
    );
  } else if (item.type === 'CARD_DESIGN_PURCHASE') {
    const isOutgoing = isOutgoingFor(item, currentTgId, currentAccountNumber);
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta">
          <span>{isOutgoing ? 'Дизайн карты' : 'Поступление (Дизайн карты)'}</span>
          <span>{date}</span>
        </div>
        <div className="history-body">
          {isOutgoing ? (
            <>
              Счет #{item.from_account}{item.to_account ? <> на счет #{item.to_account}</> : null} <br/>
              <span style={{color: 'var(--danger)', fontWeight: 'bold'}}>-{item.amount} сц</span>
            </>
          ) : (
            <>
              От счета #{item.from_account} на счет #{item.to_account} <br/>
              <span style={{color: 'var(--success)', fontWeight: 'bold'}}>+{item.amount} сц</span>
            </>
          )}
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '4px'}}>
            <div style={{fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', flex: 1, wordBreak: 'break-word'}}>{item.comment}</div>
            {receiptBtn}
          </div>
        </div>
      </div>
    );
  } else if (item.type === 'ORDER_PAYMENT') {
    let orderStatusText = '⏳ В обработке';
    let orderStatusColor = 'var(--warning)';
    if (item.order_status === 'DELIVERED') { orderStatusText = '✅ Доставлено'; orderStatusColor = 'var(--success)'; }
    else if (item.order_status === 'CANCELLED') { orderStatusText = '❌ Отменён'; orderStatusColor = 'var(--danger)'; }
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta">
          <span>Оформление заказа</span>
          <span>{date}</span>
        </div>
        <div className="history-body">
          Счет #{item.from_account} <br/>
          <span style={{color: 'var(--danger)', fontWeight: 'bold'}}>-{item.amount} сц</span><br/>
          Статус: <span style={{color: orderStatusColor, fontWeight: 'bold'}}>{orderStatusText}</span>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '4px'}}>
            <div style={{fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', flex: 1, wordBreak: 'break-word'}}>{item.comment}</div>
            {receiptBtn}
          </div>
        </div>
      </div>
    );
  } else if (item.type === 'ORDER_REFUND') {
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta">
          <span>Возврат за заказ</span>
          <span>{date}</span>
        </div>
        <div className="history-body">
          На счет #{item.to_account} <br/>
          <span style={{color: 'var(--success)', fontWeight: 'bold'}}>+{item.amount} сц</span>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '4px'}}>
            <div style={{fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', flex: 1, wordBreak: 'break-word'}}>{item.comment}</div>
            {receiptBtn}
          </div>
        </div>
      </div>
    );
  } else if (item.type === 'TREASURY_INCOME') {
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta">
          <span>Поступление в казну</span>
          <span>{date}</span>
        </div>
        <div className="history-body">
          На счет #{item.to_account} <br/>
          <span style={{color: 'var(--success)', fontWeight: 'bold'}}>+{item.amount} сц</span>
          {item.comment && <div style={{fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', marginTop: '4px'}}>{item.comment}</div>}
        </div>
      </div>
    );
  } else if (item.type === 'MARKET_SALE') {
    return (
      <div key={item.unique_id} className={itemClass}>
        <div className="history-meta">
          <span>Продажа товара</span>
          <span>{date}</span>
        </div>
        <div className="history-body">
          На счет #{item.to_account} <br/>
          <span style={{color: 'var(--success)', fontWeight: 'bold'}}>+{item.amount} сц</span>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '4px'}}>
            <div style={{fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', flex: 1, wordBreak: 'break-word'}}>{item.comment}</div>
            {receiptBtn}
          </div>
        </div>
      </div>
    );
  }
  return null;
}

/**
 * Модалка чека. Одинаковая и в банке игрока, и в админ-панели.
 * props:
 *  - receiptData: объект из buildReceiptData
 *  - onClose()
 *  - onCopyReceipt(reqId): опционально — копирование номера квитанции в буфер
 */
export function ReceiptModal({ receiptData, onClose, onCopyReceipt }) {
  const receiptRef = useRef(null);
  const [isSharing, setIsSharing] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareOk, setShareOk] = useState('');
  // Готовый заранее (пока игрок просто смотрит на чек) canvas, а из него — PNG
  // File для локального сохранения/Web Share. Держим наготове, чтобы по клику
  // не запускать тяжёлый html2canvas и не терять активацию жеста.
  const preparedFileRef = useRef(null);
  const preparedCanvasRef = useRef(null);

  // Рендерим чек СРАЗУ, как только он открылся на экране, а не по клику: так
  // html2canvas успевает отработать заранее, и отправка по клику происходит без
  // ощутимой паузы.
  useEffect(() => {
    preparedFileRef.current = null;
    preparedCanvasRef.current = null;
    setShareError('');
    setShareOk('');
    if (!receiptData) return undefined;
    let cancelled = false;
    // requestAnimationFrame — ждём, пока браузер реально отрисует чек на
    // экране, иначе html2canvas рискует захватить пустой/неполный кадр.
    const raf = requestAnimationFrame(() => {
      if (cancelled || !receiptRef.current) return;
      renderReceiptCanvas(receiptRef.current)
        .then(async (canvas) => {
          if (cancelled) return;
          preparedCanvasRef.current = canvas;
          try {
            const pngBlob = await canvasToBlob(canvas, 'image/png');
            if (!cancelled) preparedFileRef.current = new File([pngBlob], 'receipt.png', { type: 'image/png' });
          } catch (pngErr) {
            console.error('Не удалось подготовить PNG чека:', pngErr);
          }
        })
        .catch((err) => console.error('Не удалось подготовить чек заранее:', err));
    });
    return () => { cancelled = true; cancelAnimationFrame(raf); };
  }, [receiptData]);

  const handleShareReceipt = async () => {
    if (!receiptRef.current || isSharing) return;
    setIsSharing(true);
    setShareError('');
    setShareOk('');
    try {
      // Готовим и PNG (для скачивания/Web Share), и canvas (для JPEG боту).
      // Обычно всё уже отрендерено заранее (см. useEffect) — по клику просто
      // берём готовое. Если игрок кликнул раньше, чем чек успел отрисоваться
      // (доли секунды) — рендерим на месте.
      let canvas = preparedCanvasRef.current;
      let file = preparedFileRef.current;
      if (!canvas) {
        try {
          canvas = await renderReceiptCanvas(receiptRef.current);
          preparedCanvasRef.current = canvas;
        } catch (renderErr) {
          console.error('Ошибка отрисовки чека:', renderErr);
          throw new Error('Не удалось подготовить чек к отправке, попробуйте ещё раз.');
        }
      }
      if (!file) {
        try {
          const pngBlob = await canvasToBlob(canvas, 'image/png');
          file = new File([pngBlob], 'receipt.png', { type: 'image/png' });
        } catch (pngErr) {
          console.error('Ошибка PNG чека:', pngErr);
        }
      }

      let shared = false;

      // ОСНОВНОЙ, НАДЁЖНЫЙ способ: бот сам отправляет фото чека прямо в личный
      // чат игрока (sendPhoto по tg_id, который приложение и так знает). Это
      // работает всегда и не зависит ни от инлайн-режима, ни от Web Share, ни
      // от того, "протух" ли пользовательский жест. Готовый чек прилетает в чат
      // с ботом, откуда игрок пересылает его кому угодно в пару тапов.
      // Раньше пытались через switchInlineQuery + инлайн-фото, но Telegram
      // требует для инлайн-фото JPEG и публичную ссылку, и на практике результат
      // часто не появлялся — в поле ввода зависал текст "@бот <id>", а фото не
      // отправлялось. Прямая отправка ботом убирает этот класс проблем целиком.
      const tgId = getCurrentTgId();
      if (tgId) {
        try {
          const jpegDataUrl = await canvasToJpegDataUrl(canvas);
          const resp = await fetch('/api/bank/send-receipt', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tg_id: tgId, image_base64: jpegDataUrl })
          });
          const data = await resp.json().catch(() => ({}));
          if (!resp.ok || !data.success) throw new Error(data.error || 'Не удалось отправить чек');
          shared = true;
          setShareOk('Чек отправлен вам в чат с ботом — перешлите его кому нужно.');
        } catch (sendErr) {
          console.error('Ошибка отправки чека ботом:', sendErr);
        }
      }

      // ЗАПАСНОЙ способ (обычный браузер вне Telegram): системный Web Share.
      if (!shared && file && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await Promise.race([
            navigator.share({ files: [file] }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Таймаут отправки')), 15000))
          ]);
          shared = true;
        } catch (shareErr) {
          if (shareErr && shareErr.name === 'AbortError') shared = true;
          else console.error('Ошибка navigator.share:', shareErr);
        }
      }

      // Последний фоллбэк: открыть картинку отдельной страницей / скачать.
      if (!shared && file) {
        const url = URL.createObjectURL(file);
        const opened = window.open(url, '_blank');
        if (!opened) {
          const link = document.createElement('a');
          link.href = url;
          link.download = 'receipt.png';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        setShareError('Не удалось отправить чек ботом — картинка открыта отдельно, зажмите её, чтобы сохранить или переслать.');
      } else if (!shared) {
        setShareError('Не удалось подготовить чек к отправке, попробуйте ещё раз.');
      }
    } catch (err) {
      console.error('Ошибка отправки чека:', err);
      setShareError(err && err.message ? err.message : 'Не удалось поделиться чеком, попробуйте ещё раз.');
    } finally {
      setIsSharing(false);
    }
  };

    if (!receiptData) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="receipt-modal" onClick={e => e.stopPropagation()} style={{ padding: 0 }}>
        {/* Всё, что попадает в PNG при шаринге чека (см. handleShareReceipt) — от
            логотипа сверху до печати снизу включительно, БЕЗ кнопок под ним.
            Паддинг перенесён с .receipt-modal сюда — благодаря этому ширина
            захваченной картинки в точности равна ширине модалки (раньше картинка
            была уже модалки на величину паддинга .receipt-modal). Нижний паддинг
            намеренно больше остальных — это то самое "чуть ниже печати нижний
            край" запаса под фото, а кнопки при этом в захват не попадают, т.к.
            они лежат в отдельном div ниже, вне receiptRef. */}
        <div ref={receiptRef} style={{ background: 'var(--surface)', borderRadius: '20px', padding: '22px 22px 34px 22px' }}>
        <div className="receipt-logo">Azon банк</div>
        <div className="receipt-row">
          <span style={{color: 'var(--text-secondary)'}}>Операция</span>
          <span>{receiptData.title}</span>
        </div>
        <div className="receipt-row">
          <span style={{color: 'var(--text-secondary)'}}>Дата</span>
          <span>{receiptData.date}</span>
        </div>
        <div className="receipt-row total">
          <span>Итого</span>
          <span>{receiptData.amount} сц</span>
        </div>
        <div className="receipt-row">
          <span>Статус</span>
          <span style={{fontWeight: 'bold', color: receiptData.status === 'Успешно' ? 'var(--success)' : (receiptData.status === 'Отклонено' ? 'var(--danger)' : 'var(--warning)')}}>{receiptData.status}</span>
        </div>
        <div className="receipt-row">
          <span>Номер квитанции</span>
          <span
            onClick={() => onCopyReceipt && onCopyReceipt(receiptData.reqId)}
            style={{cursor: onCopyReceipt ? 'pointer' : 'default', textDecoration: onCopyReceipt ? 'underline' : 'none', color: 'var(--accent-dark)', wordBreak: 'break-all', textAlign: 'right', maxWidth: '60%'}}
          >{receiptData.reqId}</span>
        </div>

        {receiptData.type === 'TRANSFER' || receiptData.type === 'FINE_PAYMENT' ? (
          <>
            <div className="receipt-row">
              <span>Счёт списания</span>
              <span>#{receiptData.senderAccount}</span>
            </div>
            {receiptData.receiverAccount ? (
              <div className="receipt-row">
                <span>Счёт пополнения</span>
                <span>#{receiptData.receiverAccount}</span>
              </div>
            ) : (
              <div className="receipt-row">
                <span>Назначение</span>
                <span>Оплата услуги Azon</span>
              </div>
            )}
            {receiptData.comment && (
              <div className="receipt-row">
                <span>Комментарий</span>
                <span style={{textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word'}}>{receiptData.comment}</span>
              </div>
            )}
          </>
        ) : receiptData.type === 'AD_PURCHASE' || receiptData.type === 'ITEM_PROMOTION' || receiptData.type === 'CARD_DESIGN_PURCHASE' ? (
          <>
            <div className="receipt-row">
              <span>Счёт списания</span>
              <span>#{receiptData.senderAccount}</span>
            </div>
            {receiptData.receiverAccount && (
              <div className="receipt-row">
                <span>Счёт пополнения</span>
                <span>#{receiptData.receiverAccount}</span>
              </div>
            )}
            {receiptData.comment && (
              <div className="receipt-row">
                <span>Комментарий</span>
                <span style={{textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word'}}>{receiptData.comment}</span>
              </div>
            )}
          </>
        ) : receiptData.type === 'ORDER_PAYMENT' ? (
          <>
            <div className="receipt-row">
              <span>Счёт списания</span>
              <span>#{receiptData.account}</span>
            </div>
            {receiptData.comment && (
              <div className="receipt-row">
                <span>Комментарий</span>
                <span style={{textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word'}}>{receiptData.comment}</span>
              </div>
            )}
          </>
        ) : (receiptData.type === 'AD_REFUND' || receiptData.type === 'ORDER_REFUND' || receiptData.type === 'MARKET_SALE') ? (
          <>
            <div className="receipt-row">
              <span>Счёт пополнения</span>
              <span>#{receiptData.account}</span>
            </div>
            {receiptData.comment && (
              <div className="receipt-row">
                <span>Комментарий</span>
                <span style={{textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word'}}>{receiptData.comment}</span>
              </div>
            )}
          </>
        ) : (receiptData.type === 'WITHDRAW' || receiptData.type === 'DEPOSIT_REQ') ? (
          <>
            <div className="receipt-row">
              <span>{receiptData.type === 'WITHDRAW' ? 'Счёт списания' : 'Счёт пополнения'}</span>
              <span>#{receiptData.account}</span>
            </div>
            <div className="receipt-row">
              <span>Способ</span>
              <span>{receiptData.destType}</span>
            </div>
            <div className="receipt-row">
              <span>Реквизиты</span>
              <span style={{textAlign: 'right', maxWidth: '60%', wordBreak: 'break-all'}}>{receiptData.destValue}</span>
            </div>
            {receiptData.status === 'Отклонено' && receiptData.reason && (
              <div className="receipt-row">
                <span>Причина</span>
                <span style={{textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word', color: 'var(--danger)'}}>{receiptData.reason}</span>
              </div>
            )}
          </>
        ) : (
          <div className="receipt-row">
            <span>Счёт</span>
            <span>#{receiptData.account}</span>
          </div>
        )}

        <div style={{textAlign: 'center', fontSize: '12px', color: 'var(--text-secondary)', marginTop: '20px', marginBottom: '10px'}}>
          Служба поддержки Azon банка @zolartem
        </div>
        <div className="receipt-stamp">
          AZON БАНК
          <div style={{fontSize: '10px', fontWeight: 'normal', textTransform: 'none', letterSpacing: 'normal', marginTop: '2px'}}>made in Admag</div>
        </div>
        </div>
        <div style={{display: 'flex', gap: '10px', padding: '0 22px 22px 22px'}}>
          <button className="secondary-btn" style={{flex: 1}} onClick={handleShareReceipt} disabled={isSharing}>{isSharing ? 'Готовим...' : 'Поделиться'}</button>
          <button className="primary-btn" style={{flex: 1}} onClick={onClose}>Закрыть</button>
        </div>
        {shareOk && (
          <p style={{fontSize: '12px', color: 'var(--success)', textAlign: 'center', padding: '0 22px', marginTop: '-8px', marginBottom: '22px'}}>{shareOk}</p>
        )}
        {shareError && (
          <p style={{fontSize: '12px', color: 'var(--danger)', textAlign: 'center', padding: '0 22px', marginTop: '-8px', marginBottom: '22px'}}>{shareError}</p>
        )}
      </div>
    </div>
  );
}